import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/client';
import { tenantsApi } from '@/api/tenants';
import type { TenantMembership } from '@/api/auth';
import Modal from '@/components/Modal';

export function CreateSpaceModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (t: TenantMembership) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      tenantsApi.create({
        name: name.trim(),
        slug: slug.trim() || undefined,
      }),
    onSuccess: (t) => onCreated(t),
    onError: (err) => {
      setError(errorMessage(err));
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError(t('shell_ui.create_space.name_required'));
      return;
    }
    create.mutate();
  };

  const title = <h2 className="display-title-sm">{t('nav.create_space')}</h2>;

  return (
    <Modal open onClose={onClose} title={title} maxWidth="max-w-md">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label htmlFor="layout-1" className="section-label no-dot mb-1.5">{t('shell_ui.create_space.name_label')}</label>
          <input id="layout-1"
            className="input"
            placeholder={t('shell_ui.create_space.name_placeholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            maxLength={80}
            disabled={create.isPending}
          />
        </div>
        <div>
          <label htmlFor="layout-2" className="section-label no-dot mb-1.5">{t('shell_ui.create_space.slug_label')}</label>
          <input id="layout-2"
            className="input font-mono"
            placeholder={t('shell_ui.create_space.slug_placeholder')}
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            pattern="[a-z0-9][a-z0-9-]*[a-z0-9]"
            maxLength={64}
            disabled={create.isPending}
          />
          <div className="text-[10px] mt-1" style={{ color: 'var(--text-dim)' }}>
            {t('shell_ui.create_space.slug_hint')}
          </div>
        </div>
        {error && (
          <div
            className="text-[13px] rounded px-3 py-2"
            style={{
              color: 'var(--danger)',
              background: 'var(--danger-bg)',
              border: '1px solid var(--danger)',
            }}
          >
            {error}
          </div>
        )}
        <div className="flex gap-2 justify-end pt-2">
          <button
            type="button"
            className="btn-outline"
            onClick={onClose}
            disabled={create.isPending}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={create.isPending}
          >
            {create.isPending ? t('shell_ui.create_space.creating') : t('common.create')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
